import { useRouter } from "next/router";
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import useQueryString, {
  MESSAGE_ID_QUERY_STRING,
  MESSAGE_THREAD_ID_QUERY_STRING,
} from "modules/hooks/use-query-string";

interface IContextProps {
  setMessageThreadIds: (messageThreadIds: string[]) => void;
  advanceToNextAvailableMessageThread: () => void;
}

const Context = createContext<IContextProps>({
  setMessageThreadIds: () => {},
  advanceToNextAvailableMessageThread: () => {},
});

export interface IProviderProps {
  children?: ReactNode;
}

export const MessageThreadNavigationProvider = ({
  children,
}: IProviderProps) => {
  const [messageThreadIds, setMessageThreadIds] = useState<string[]>([]);
  const { push, query } = useRouter();
  const { qsMessageThreadId } = useQueryString();

  const firstMessageThreadId = useMemo(
    () => (messageThreadIds.length ? messageThreadIds[0] : undefined),
    [messageThreadIds],
  );

  const previousMessageThreadId = useMemo(() => {
    const i = messageThreadIds.findIndex((id) => id === qsMessageThreadId);
    if (i > 0) {
      return messageThreadIds[i - 1];
    }
  }, [qsMessageThreadId, messageThreadIds]);

  const nextMessageThreadId = useMemo(() => {
    const i = messageThreadIds.findIndex((id) => id === qsMessageThreadId);
    if (i < messageThreadIds.length - 1) {
      return messageThreadIds[i + 1];
    }
  }, [qsMessageThreadId, messageThreadIds]);

  const advanceToNextAvailableMessageThread = useCallback(() => {
    const nextId = !qsMessageThreadId
      ? firstMessageThreadId
      : nextMessageThreadId || previousMessageThreadId;

    const newQuery = { ...query };
    delete newQuery[MESSAGE_ID_QUERY_STRING];
    delete newQuery[MESSAGE_THREAD_ID_QUERY_STRING];

    if (nextId) {
      newQuery[MESSAGE_THREAD_ID_QUERY_STRING] = nextId;
    }

    push({
      query: newQuery,
    });
  }, [
    qsMessageThreadId,
    firstMessageThreadId,
    nextMessageThreadId,
    previousMessageThreadId,
    push,
    query,
  ]);

  const value = useMemo<IContextProps>(
    () => ({
      setMessageThreadIds,
      advanceToNextAvailableMessageThread,
    }),
    [advanceToNextAvailableMessageThread],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useMessageThreadNavigation = (): IContextProps => {
  const context = useContext(Context);

  if (!context) {
    throw new Error(
      "useMessageThreadNavigation must be used within a MessageThreadNavigationProvider",
    );
  }

  return context;
};
